import type { PostFilterViolation } from './isPostSatisfyingFilters'

/**
 * Tracks posts whose tag-editing menu is currently open so that filtered post
 * lists keep them visible while the user changes their tags.
 *
 * Problem: in a tag-filtered view (e.g. the "Untagged" filter), adding a tag to
 * a post makes it stop matching the filter, which normally removes it from the
 * list immediately. That makes adding several tags in a row painful — the post
 * disappears after the first tag and has to be found again.
 *
 * Solution: while a post's tag menu is open we "hold" it visible. The removal
 * paths (the tag mutation's cache update and the realtime Pusher handler) skip
 * hiding held posts; the post is only removed once its menu closes. See
 * `PostCardTags` for where holds are set/released and where the deferred
 * removal happens.
 *
 * This is intentionally a module-level store rather than React context: the two
 * consumers live in different parts of the tree (a per-post component and a
 * list-level realtime hook) and only ever read the state imperatively, so a
 * shared set keyed by the globally-unique post id is the simplest fit.
 */
const heldPostIds = new Set<string>()

/**
 * Marks a post as held visible while its tags are being edited.
 */
export const holdPostVisible = (postId: string): void => {
  heldPostIds.add(postId)
}

/**
 * Releases a previously held post so it can be filtered out of lists again.
 */
export const releasePostVisible = (postId: string): void => {
  heldPostIds.delete(postId)
}

/**
 * True when a post should be kept visible regardless of filters because its
 * tags are actively being edited.
 */
export const isPostVisibilityHeld = (postId: string): boolean =>
  heldPostIds.has(postId)

/**
 * Whether a post should stay pinned in a filtered list purely because its tag
 * menu is open. True only when the post is held *and* the sole reason it fails
 * the active filters is the tag filter — so a held post that also gets sent,
 * moved, or rescheduled is still handled normally and can't get stuck out of
 * sync. The realtime handlers use this to keep the hold specific to tag edits.
 */
export const isHeldAgainstTagFilterOnly = (
  postId: string,
  filterViolations: PostFilterViolation[],
): boolean =>
  isPostVisibilityHeld(postId) &&
  filterViolations.length > 0 &&
  filterViolations.every((violation) => violation === 'tagIds')
